/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';

/*
 * This module produces WizardButton objects that maintain the state of a custom button in a wizard.
 */
factory.$inject = ['$filter', '$q', 'UtilService'];

/**
 * Defines the factory that supplies a generator used to create new WizardButton objects.
 *
 * @returns {function():WizardButton} a function that will generate a new WizardButton object when called.
 */
export function factory($filter, $q, UtilService) {
  // WizardButton is a function that produces a new object
  return {
    generate: WizardButton
  };

  /**
   * Returns a new WizardButton object.
   *
   * @returns {WizardButton}
   */
  function WizardButton() {
    var i18n = $filter('i18n');

    /**
     * The text or a function returning the text to be shown as the button label. This takes precedence over textKey.
     *
     * @type {string | function():string}
     */
    var text;

    /**
     * The I18N message key or a function returning the I18N message key of the text to be shown as the button label.
     * This is used only if 'text' is not set.
     *
     * @type {string | function():string}
     */
    var textKey;

    /**
     * The text or a function returning the text to be shown as the button tooltip. This takes precedence over
     * tooltipKey.
     *
     * @type {string | function():string}
     */
    var tooltip;

    /**
     * The I18N message key or a function returning the I18N message key of the text to be shown as the button tooltip.
     * This is used only if 'tooltip' is not set.
     *
     * @type {string | function():string}
     */
    var tooltipKey;

    /**
     * A boolean or a function returning a boolean to indicate whether the button is enabled. Defaults to true.
     *
     * @type {boolean | function():boolean}
     */
    var enabled;

    /**
     * A boolean or a function returning a boolean to indicate whether the button is visible. Defaults to true.
     *
     * @type {boolean | function():boolean}
     */
    var visible;

    /**
     * The function to be called when the button is clicked. The function is passed no arguments and should
     * return a promise that resolves to one of the following:
     *
     *   1) true if the wizard can be dismissed
     *   2) false if the wizard should remain
     *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
     *
     * If click is not defined an error will be thrown
     */
    var clickFn;

    // Expose the functions only, not the data
    return {
      click: click,
      getEnabled: getEnabled,
      getText: getText,
      getTooltip: getTooltip,
      getVisible: getVisible,
      setClick: setClick,
      setEnabled: setEnabled,
      setText: setText,
      setTextKey: setTextKey,
      setTooltip: setTooltip,
      setTooltipKey: setTooltipKey,
      setVisible: setVisible
    };

    /**
     * Clicks the button.
     *
     * @returns {Promise} a promise that resolves to one of the following:
     *   1) true if the wizard can be dismissed
     *   2) false if the wizard should remain
     *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
     */
    function click() {
      if (!getEnabled()) {
        throw new Error('Cannot click disabled button');
      }

      if (!clickFn) {
        throw new Error('The click function is not defined');
      }

      var result = clickFn();

      // Make sure the result looks like a promise
      if (!_.isObject(result) || !_.isFunction(result.then)) {
        throw new Error('Expected the click function to return a promise but found ' + typeof result);
      }

      return result;
    }

    /**
     * Sets a function that will be called when the user clicks the button. The function should return a promise that
     * resolves to one of the following:
     *   1) true if the wizard can be dismissed
     *   2) false if the wizard should remain
     *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
     *
     * @param {function():Promise} fn - a function that will be called when the user clicks the button.
     */
    function setClick(fn) {
      if (UtilService.isSet(fn)) {
        ValidationUtils.assertIsFunction(fn, 'click');
        clickFn = fn;
      } else {
        clickFn = undefined;
      }
    }

    /**
     * Determines whether the button should be enabled.
     *
     * @returns {boolean} true if the button should be enabled, false otherwise.
     */
    function getEnabled() {
      var isEnabled = UtilService.getBoolean(enabled);

      // Defaults to enabled
      return isEnabled !== false;
    }

    /**
     * Sets whether the button should be enabled.
     *
     * @param {boolean | function():boolean} booleanOrFn - The value or a function returning the value that indicates
     * whether the button should be enabled.
     */
    function setEnabled(booleanOrFn) {
      if (UtilService.isSet(booleanOrFn)) {
        ValidationUtils.assertIsBooleanOrFn(booleanOrFn, 'enabled');
        enabled = booleanOrFn;
      } else {
        enabled = undefined;
      }
    }

    /**
     * Returns the text label for the button.
     *
     * @returns {string} the text label to be shown.
     */
    function getText() {
      // Look for the value
      var str = UtilService.getString(text);
      if (str) {
        return str;
      }

      // Look for the message key
      var key = UtilService.getString(textKey);
      if (key) {
        return i18n(key);
      }

      // Defaults to something we'll notice
      return '!!!TEXT NOT FOUND!!!';
    }

    /**
     * Sets the text to be displayed as the button label.
     *
     * @param {string | function():string} textOrFn - The text or a function returning the text to be displayed as
     * the button label.
     */
    function setText(textOrFn) {
      if (UtilService.isSet(textOrFn)) {
        ValidationUtils.assertIsStringOrFn(textOrFn, 'text');
        text = textOrFn;
      } else {
        text = undefined;
      }
    }

    /**
     * Sets I18N key used to get the text to be displayed as the button label.
     *
     * @param {string | function():string} keyOrFn - The I18N key or a function returning the I18N key to be used to
     * get the text displayed as the button label.
     */
    function setTextKey(keyOrFn) {
      if (UtilService.isSet(keyOrFn)) {
        ValidationUtils.assertIsStringOrFn(keyOrFn, 'text key');
        textKey = keyOrFn;
      } else {
        textKey = undefined;
      }
    }

    /**
     * Returns the tooltip for the button.
     *
     * @returns {string} the tooltip to be shown.
     */
    function getTooltip() {
      // Look for the value
      var text = UtilService.getString(tooltip);
      if (text) {
        return text;
      }

      // Look for the message key
      var key = UtilService.getString(tooltipKey);
      if (key) {
        return i18n(key);
      }

      // Defaults to no tooltip
    }

    /**
     * Sets the text to be displayed as the button tooltip.
     *
     * @param {string | function():string} textOrFn - The text or a function returning the text to be displayed as
     * the button tooltip.
     */
    function setTooltip(textOrFn) {
      if (UtilService.isSet(textOrFn)) {
        ValidationUtils.assertIsStringOrFn(textOrFn, 'tooltip');
        tooltip = textOrFn;
      } else {
        tooltip = undefined;
      }
    }

    /**
     * Sets I18N key used to get the text to be displayed as the button tooltip.
     *
     * @param {string | function():string} keyOrFn - The I18N key or a function returning the I18N key to be used to
     * get the text displayed as the button tooltip.
     */
    function setTooltipKey(keyOrFn) {
      if (UtilService.isSet(keyOrFn)) {
        ValidationUtils.assertIsStringOrFn(keyOrFn, 'tooltip key');
        tooltipKey = keyOrFn;
      } else {
        tooltipKey = undefined;
      }
    }

    /**
     * Determines whether the button should be visible.
     *
     * @returns {boolean} true if the button should be visible, false otherwise.
     */
    function getVisible() {
      var isVisible = UtilService.getBoolean(visible);

      // Defaults to visible
      return isVisible !== false;
    }

    /**
     * Sets whether the button should be visible.
     *
     * @param {boolean | function():boolean} booleanOrFn - The value or a function returning the value that indicates
     * whether the button should be visible.
     */
    function setVisible(booleanOrFn) {
      if (UtilService.isSet(booleanOrFn)) {
        ValidationUtils.assertIsBooleanOrFn(booleanOrFn, 'visible');
        visible = booleanOrFn;
      } else {
        visible = undefined;
      }
    }
  }
}
